import { timeYear, timeMonth, timeDay } from "d3-time";
import { GetEracheDateComponents, EracheMonths } from "../../utils/dateConverters";

/** @typedef {'cycle'|'octet'|'year'|'month'|'day'} ScaleMode */

const DAY_MS = 86400000;
const ERACHE_DAY_MS = 20 * 60 * 1000;
const ERACHE_MONTH_MS = 64 * ERACHE_DAY_MS;
const ERACHE_YEAR_MS = 16 * ERACHE_MONTH_MS;
const ERACHE_OCTET_MS = 8 * ERACHE_YEAR_MS;
const ERACHE_CYCLE_MS = 64 * ERACHE_YEAR_MS;

/**
 * Pick tick granularity from visible domain duration.
 * @param {number} domainMs
 * @returns {ScaleMode}
 */
export function getScaleMode(domainMs) {
  if (!Number.isFinite(domainMs) || domainMs <= 0) return "day";
  if (domainMs > ERACHE_CYCLE_MS * 3) return "cycle";
  if (domainMs > ERACHE_OCTET_MS * 2.5) return "octet";
  if (domainMs > ERACHE_YEAR_MS * 2) return "year";
  if (domainMs > ERACHE_MONTH_MS * 3) return "month";
  return "day";
}

/**
 * Generate tick dates between viewStart and viewEnd (inclusive-ish).
 * @param {Date} viewStart
 * @param {Date} viewEnd
 * @param {ScaleMode} mode
 * @param {'irl'|'erache'} dateMode
 * @returns {{ ticks: Date[], majorEvery: (d: Date) => boolean }}
 */
export function generateAxisTicks(viewStart, viewEnd, mode, dateMode) {
  const t0 = viewStart.getTime();
  const t1 = viewEnd.getTime();
  if (t1 <= t0) {
    return { ticks: [viewStart], majorEvery: () => true };
  }

  if (dateMode === "irl") {
    return generateIrlTicks(viewStart, viewEnd, mode);
  }
  return generateEracheTicks(viewStart, viewEnd, mode);
}

function generateIrlTicks(viewStart, viewEnd, mode) {
  let interval;
  let majorEvery = () => false;

  if (mode === "cycle") {
    const step = 64 * 365.25 * DAY_MS;
    return { ticks: uniformTicks(viewStart, viewEnd, step), majorEvery: () => true };
  }
  if (mode === "octet") {
    const step = 8 * 365.25 * DAY_MS;
    return { ticks: uniformTicks(viewStart, viewEnd, step), majorEvery: () => true };
  }
  if (mode === "year") {
    interval = timeYear;
    majorEvery = () => true;
  } else if (mode === "month") {
    interval = timeMonth;
    majorEvery = (d) => d.getMonth() === 0;
  } else {
    interval = timeDay;
    majorEvery = (d) => d.getDate() === 1;
  }

  const ticks = interval.range(
    interval.floor(viewStart),
    interval.offset(interval.ceil(viewEnd), 1)
  );
  return { ticks: ticks.filter((d) => d >= viewStart && d <= viewEnd), majorEvery };
}

function uniformTicks(viewStart, viewEnd, stepMs) {
  const t0 = viewStart.getTime();
  const t1 = viewEnd.getTime();
  const start = Math.floor(t0 / stepMs) * stepMs;
  const out = [];
  for (let t = start; t <= t1 + stepMs; t += stepMs) {
    const d = new Date(t);
    if (d >= viewStart && d <= viewEnd) out.push(d);
  }
  if (!out.length) out.push(new Date(t0));
  return out;
}

function generateEracheTicks(viewStart, viewEnd, mode) {
  let stepMs;
  let majorEvery = () => false;

  if (mode === "cycle") {
    stepMs = ERACHE_CYCLE_MS;
    majorEvery = () => true;
  } else if (mode === "octet") {
    stepMs = ERACHE_OCTET_MS;
    majorEvery = () => true;
  } else if (mode === "year") {
    stepMs = ERACHE_YEAR_MS;
    majorEvery = () => true;
  } else if (mode === "month") {
    stepMs = ERACHE_MONTH_MS;
    majorEvery = (d) => {
      const er = GetEracheDateComponents(d);
      return er ? er.month === 1 : false;
    };
  } else {
    stepMs = ERACHE_DAY_MS;
    majorEvery = (d) => {
      const er = GetEracheDateComponents(d);
      return er ? er.day === 1 : false;
    };
  }

  const ticks = uniformTicks(viewStart, viewEnd, stepMs);
  return { ticks, majorEvery };
}

/**
 * Short tick label for axis.
 * @param {Date} date
 * @param {ScaleMode} mode
 * @param {'irl'|'erache'} dateMode
 */
export function formatAxisTickLabel(date, mode, dateMode) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  if (dateMode === "irl") {
    if (mode === "day") return String(date.getDate());
    if (mode === "month") {
      const mo = date.toLocaleString("en", { month: "short" });
      return `${mo} ${date.getFullYear()}`;
    }
    if (mode === "year") return String(date.getFullYear());
    if (mode === "octet") return String(date.getFullYear());
    return String(date.getFullYear());
  }

  const er = GetEracheDateComponents(date);
  if (!er) return "";

  if (mode === "day") return String(er.day);
  if (mode === "month") {
    const m3 = (EracheMonths[er.month - 1] || "").slice(0, 3);
    return `${m3} ${er.year}`;
  }
  if (mode === "year") return `An ${er.year}`;
  if (mode === "octet") return `An ${er.year}`;
  return `An ${er.year}`;
}

const TWO_IRL_YEARS_MS = 2 * 365.25 * DAY_MS;

/** When the visible window is shorter than ~2 IRL years, axis month ticks may not show year — use sticky label. */
export function shouldShowStickyYear(viewStart, viewEnd) {
  if (!viewStart || !viewEnd) return false;
  return viewEnd.getTime() - viewStart.getTime() < TWO_IRL_YEARS_MS;
}

/**
 * @param {Date} viewStart
 * @param {'irl'|'erache'} dateMode
 */
export function stickyYearLabelFromViewStart(viewStart, dateMode) {
  if (!(viewStart instanceof Date) || Number.isNaN(viewStart.getTime())) return "";
  if (dateMode === "irl") return String(viewStart.getFullYear());
  const er = GetEracheDateComponents(viewStart);
  return er ? `An ${er.year}` : "";
}
